import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchProjectsByCategory, getProjectBySlug } from '../lib/projects';
import { getFileUrl } from '../lib/pb';
import { 
  MapPin, Calendar, Tag, ArrowLeft, ZoomIn, 
  ChevronLeft, ChevronRight, X, Phone, MessageSquare,
  ShieldCheck, ClipboardList, Info, ArrowRight, Camera
} from 'lucide-react';

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const item = await getProjectBySlug(slug);
        if (!cancelled) setProject(item);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Réalisation introuvable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!project || !project.category) {
      setSimilar([]);
      return;
    }
    let cancelled = false;
    async function loadSimilar() {
      try {
        const list = await fetchProjectsByCategory(project.category);
        if (cancelled) return;
        const filtered = (list || []).filter(p => p.id !== project.id && p.slug);
        setSimilar(filtered.slice(0, 4));
      } catch (e) {
        if (!cancelled) setSimilar([]);
      }
    }
    loadSimilar();
    return () => { cancelled = true; };
  }, [project]);

  const images = Array.isArray(project?.gallery) ? project.gallery : [];

  const workflowSteps = [
    { title: "Prise de contact", desc: "Présentation de votre besoin via téléphone ou formulaire.", icon: <Phone size={18}/> },
    { title: "Visite sur site", desc: "Mesures et conseils techniques à domicile.", icon: <MapPin size={18}/> },
    { title: "Devis détaillé", desc: "Proposition chiffrée adaptée à votre budget.", icon: <ClipboardList size={18}/> },
    { title: "Mise en service", desc: "Installation et réglages pour une utilisation sereine.", icon: <ShieldCheck size={18}/> }
  ];

  if (loading) return <div className="loading-fullscreen">Chargement de l'œuvre...</div>;
  if (!project && !loading) return <div className="error-fullscreen">Projet introuvable</div>;

  return (
    <div className="project-detail-signature">
      
      {/* CINEMATIC HERO */}
      <section className="project-hero-premium">
        <div className="hero-bg-wrapper">
           <img src={getFileUrl(project, images[0])} alt={project.title} className="hero-blur-bg" />
           <div className="hero-overlay"></div>
        </div>
        <div className="container-premium hero-content-alignment">
           <button onClick={() => navigate(-1)} className="back-btn-premium">
              <ArrowLeft size={20}/>
              <span>Retour</span>
           </button>
           <div className="hero-text-box">
              <div className="project-cat-tag">{project.expand?.category?.name || 'Réalisation'}</div>
              <h1>{project.title}</h1>
              <div className="hero-meta-row">
                 {project.location && <div className="meta-item"><MapPin size={16}/><span>{project.location}</span></div>}
                 {project.date && <div className="meta-item"><Calendar size={16}/><span>{new Date(project.date).getFullYear()}</span></div>}
              </div>
           </div>
        </div>
      </section>

      <div className="container-premium main-project-layout">
         
         {/* GALLERY SECTION */}
         <div className="gallery-column-premium">
            <div className="main-image-frame shadow-signature" onClick={() => setLightboxOpen(true)}>
               <img src={getFileUrl(project, images[activeImageIndex])} alt={project.title} />
               <div className="zoom-indicator"><ZoomIn size={24}/></div>
            </div>
            {images.length > 1 && (
               <div className="thumbnail-strip">
                  {images.map((img, idx) => (
                     <div key={idx} 
                          className={`thumb-box ${idx === activeImageIndex ? 'active' : ''}`}
                          onClick={() => setActiveImageIndex(idx)}>
                        <img src={getFileUrl(project, img)} alt={`Détail ${idx}`} />
                     </div>
                  ))}
               </div>
            )}
         </div>

         {/* CONTENT COLUMN */}
         <div className="info-column-premium">
            
            <div className="description-card shadow-signature">
               <h2 className="title-with-bar">À propos de l'installation</h2>
               <div className="project-text-content">
                  {project.description || "Aucun détail technique supplémentaire n'est disponible pour ce projet."}
               </div>
               <div className="project-specs-grid">
                  <div className="spec-item">
                     <div className="spec-icon"><Tag size={18}/></div>
                     <div className="spec-txt"><h3>Catégorie</h3><p>{project.expand?.category?.name || 'Non classé'}</p></div>
                  </div>
                  <div className="spec-item">
                     <div className="spec-icon"><MapPin size={18}/></div>
                     <div className="spec-txt"><h3>Lieu</h3><p>{project.location || 'Gers et environs'}</p></div>
                  </div>
               </div>
            </div>

            {/* SMALL WORKFLOW PREVIEW */}
            <div className="workflow-sidebar-card shadow-signature">
               <h3>Votre projet vous attend</h3>
               <div className="mini-workflow">
                  {workflowSteps.map((step, idx) => (
                    <div key={idx} className="mini-step">
                       <div className="mini-icon">{step.icon}</div>
                       <div className="mini-txt">
                          <h4>{step.title}</h4>
                       </div>
                    </div>
                  ))}
               </div>
               <Link to="/contact" className="btn-sidebar-gold">
                  <span>Demander mon devis</span>
                  <ArrowRight size={18}/>
               </Link>
            </div>

         </div>

      </div>

      {/* SIMILAR PROJECTS SECTION */}
      {similar.length > 0 && (
         <section className="similar-projects-section">
            <div className="container-premium">
               <h2 className="section-title-premium">Autres réalisations de prestige</h2>
               <div className="similar-grid-premium">
                  {similar.map(p => (
                     <Link key={p.id} to={`/realisations/${p.slug}`} className="similar-card shadow-signature">
                        <img src={getFileUrl(p, p.gallery?.[0])} alt={p.title} />
                        <div className="similar-overlay">
                           <h4>{p.title}</h4>
                           <p>{p.location}</p>
                        </div>
                     </Link>
                  ))}
               </div>
            </div>
         </section>
      )}

      {/* LIGHTBOX */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
           <button className="close-lightbox"><X size={32}/></button>
           <div className="lightbox-content" onClick={e => e.stopPropagation()}>
              <img src={getFileUrl(project, images[activeImageIndex])} alt="Zoom" />
              {images.length > 1 && (
                 <>
                   <button className="nav-light prev" onClick={() => setActiveImageIndex((activeImageIndex - 1 + images.length) % images.length)}><ChevronLeft size={40}/></button>
                   <button className="nav-light next" onClick={() => setActiveImageIndex((activeImageIndex + 1) % images.length)}><ChevronRight size={40}/></button>
                 </>
              )}
           </div>
        </div>
      )}

      <style jsx>{`
        .project-detail-signature { background: #fcfcfd; min-height: 100vh; padding-bottom: 100px; font-family: 'Inter', sans-serif; }
        .container-premium { maxWidth: 1200px; margin: 0 auto; padding: 0 20px; }

        /* HERO SECTION */
        .project-hero-premium { position: relative; height: 60vh; min-height: 450px; display: flex; align-items: flex-end; overflow: hidden; }
        .hero-bg-wrapper { position: absolute; inset: 0; z-index: 1; }
        .hero-blur-bg { width: 100%; height: 100%; object-fit: cover; filter: brightness(0.6) saturate(1.2); transform: scale(1.05); }
        .hero-overlay { position: absolute; inset: 0; background: linear-gradient(0deg, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.2) 100%); }
        
        .hero-content-alignment { position: relative; z-index: 5; padding-bottom: 60px; display: flex; flex-direction: column; gap: 40px; }
        .back-btn-premium { width: fit-content; display: flex; align-items: center; gap: 10px; background: rgba(255,255,255,0.1); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.2); color: #fff; padding: 10px 20px; border-radius: 12px; cursor: pointer; transition: all 0.2s; font-weight: 700; }
        .back-btn-premium:hover { background: rgba(255,255,255,0.2); transform: translateX(-5px); }

        .hero-text-box h1 { font-size: clamp(32px, 5vw, 56px); font-weight: 950; color: #fff; margin-bottom: 15px; letter-spacing: -2px; }
        .project-cat-tag { width: fit-content; padding: 6px 16px; background: var(--accent); color: #fff; border-radius: 99px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 20px; }
        .hero-meta-row { display: flex; gap: 25px; color: #94a3b8; font-size: 15px; font-weight: 600; }
        .meta-item { display: flex; align-items: center; gap: 8px; }

        /* MAIN LAYOUT */
        .main-project-layout { display: grid; grid-template-columns: 1.3fr 1fr; gap: 60px; margin-top: -40px; position: relative; z-index: 10; }
        
        /* GALLERY */
        .main-image-frame { border-radius: 40px; overflow: hidden; background: #fff; aspect-ratio: 4/3; position: relative; cursor: pointer; }
        .main-image-frame img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
        .main-image-frame:hover img { transform: scale(1.03); }
        .zoom-indicator { position: absolute; bottom: 30px; right: 30px; width: 60px; height: 60px; border-radius: 50%; background: var(--accent); color: #fff; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 20px rgba(0,0,0,0.2); }

        .thumbnail-strip { display: flex; gap: 15px; margin-top: 20px; overflow-x: auto; padding-bottom: 10px; }
        .thumb-box { width: 100px; height: 100px; border-radius: 20px; overflow: hidden; cursor: pointer; border: 2px solid transparent; transition: all 0.2s; opacity: 0.6; flex-shrink: 0; }
        .thumb-box img { width: 100%; height: 100%; object-fit: cover; }
        .thumb-box.active { border-color: var(--accent); opacity: 1; transform: translateY(-5px); }
        .thumb-box:hover { opacity: 1; }

        /* CONTENT */
        .description-card { background: #fff; border-radius: 40px; padding: 50px; margin-bottom: 40px; }
        .title-with-bar { font-size: 20px; font-weight: 900; color: #0f172a; margin-bottom: 30px; position: relative; }
        .title-with-bar::after { content: ''; position: absolute; bottom: -8px; left: 0; width: 40px; height: 3px; background: var(--accent); border-radius: 99px; }
        .project-text-content { font-size: 16px; color: #4b5563; line-height: 1.8; margin-bottom: 40px; white-space: pre-line; }

        .project-specs-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; padding-top: 30px; border-top: 1px solid #f1f5f9; }
        .spec-item { display: flex; align-items: center; gap: 15px; }
        .spec-icon { width: 44px; height: 44px; border-radius: 12px; background: #f8fafc; display: flex; align-items: center; justify-content: center; color: var(--accent); }
        .spec-txt h3 { font-size: 11px; font-weight: 800; text-transform: uppercase; color: #94a3b8; margin: 0; }
        .spec-txt p { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0; }

        /* SIDEBAR CARD */
        .workflow-sidebar-card { background: #0f172a; border-radius: 40px; padding: 40px; color: #fff; }
        .workflow-sidebar-card h3 { font-size: 20px; font-weight: 900; margin-bottom: 25px; }
        .mini-workflow { display: flex; flex-direction: column; gap: 15px; margin-bottom: 30px; }
        .mini-step { display: flex; align-items: center; gap: 15px; }
        .mini-icon { width: 34px; height: 34px; border-radius: 50%; border: 1px solid rgba(34, 197, 94, 0.5); color: var(--accent); display: flex; align-items: center; justify-content: center; }
        .mini-txt h4 { font-size: 14px; font-weight: 700; color: #e2e8f0; margin: 0; }
        .btn-sidebar-emerald { display: flex; align-items: center; justify-content: space-between; padding: 16px 25px; background: var(--accent); color: #fff; border-radius: 18px; text-decoration: none; font-weight: 800; transition: all 0.3s; }
        .btn-sidebar-emerald:hover { transform: translateY(-5px); background: var(--accent-600); box-shadow: 0 10px 20px rgba(0,0,0,0.3); }

        /* SIMILAR PROJECTS */
        .similar-projects-section { padding: 100px 0; background: #f0fdf4; }
        .section-title-premium { font-size: 24px; font-weight: 950; color: #0f172a; margin-bottom: 40px; text-align: center; }
        .similar-grid-premium { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 30px; }
        .similar-card { position: relative; border-radius: 30px; overflow: hidden; aspect-ratio: 4/3; display: block; }
        .similar-card img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
        .similar-overlay { position: absolute; inset: 0; background: linear-gradient(0deg, rgba(15,23,42,0.8) 0%, transparent 60%); display: flex; flex-direction: column; justify-content: flex-end; padding: 25px; opacity: 1; transition: all 0.4s; }
        .similar-overlay h4 { font-size: 16px; font-weight: 900; color: #fff; margin-bottom: 5px; }
        .similar-overlay p { font-size: 12px; color: #94a3b8; margin: 0; }
        .similar-card:hover img { transform: scale(1.1); }
        .similar-card:hover .similar-overlay { background: linear-gradient(0deg, var(--accent) 0%, transparent 60%); }

        /* LIGHTBOX */
        .lightbox-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.95); z-index: 1000; display: flex; align-items: center; justify-content: center; }
        .lightbox-content { position: relative; maxWidth: 90vw; maxHeight: 90vh; }
        .lightbox-content img { maxWidth: 100%; maxHeight: 90vh; display: block; border-radius: 10px; }
        .close-lightbox { position: absolute; top: -60px; right: 0; background: transparent; border: none; color: #fff; cursor: pointer; }
        .nav-light { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.1); border: none; color: #fff; width: 60px; height: 60px; border-radius: 50%; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; justify-content: center; }
        .nav-light:hover { background: var(--accent); }
        .nav-light.prev { left: -80px; }
        .nav-light.next { right: -80px; }

        .shadow-signature { box-shadow: 0 20px 50px -12px rgba(15,23,42,0.08); }

        @media (max-width: 991px) {
          .main-project-layout { grid-template-columns: 1fr; gap: 40px; }
          .nav-light.prev { left: 10px; }
          .nav-light.next { right: 10px; }
          .nav-light { width: 44px; height: 44px; }
        }
      `}</style>
    </div>
  );
}
